/**
 * Service for reading and writing user preferences to the backend.
 */
import { IPublicClientApplication } from '@azure/msal-browser';
import { getAccessTokenHelper } from './msalAuth';

// --- Types ---

export interface UserPreferencesPayload {
  modelPref?: string;
  bubbleColor?: string;
  fontSize?: string;
  guidedTutorial?: boolean;
}

export interface UserPreferencesResponse extends UserPreferencesPayload {}

// --- Service ---

export class UserPreferencesService {
  private static readonly ENDPOINT = `${process.env.REACT_APP_APIM_BASE_URL || 'https://navigatordev.ai.3m.com'}/nav-api/user-preferences`;

  private msalInstance: IPublicClientApplication;

  constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  private async getAccessToken(): Promise<string> {
    return getAccessTokenHelper(this.msalInstance);
  }

  /**
   * Fetch all user preferences from the backend.
   */
  public async getUserPreferences(): Promise<UserPreferencesResponse> {
    const accessToken = await this.getAccessToken();

    const response = await fetch(UserPreferencesService.ENDPOINT, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.detail ?? `GET user-preferences failed with status ${response.status}`);
    }

    return response.json() as Promise<UserPreferencesResponse>;
  }

  /**
   * Update one or more user preferences. The payload does not need to include all fields —
   * only the fields being changed are required.
   */
  public async updateUserPreferences(prefs: UserPreferencesPayload): Promise<void> {
    const accessToken = await this.getAccessToken();

    const response = await fetch(UserPreferencesService.ENDPOINT, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(prefs),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.detail ?? `PUT user-preferences failed with status ${response.status}`);
    }
  }
}

// --- Factory ---

export const createUserPreferencesService = (
  msalInstance: IPublicClientApplication
): UserPreferencesService => new UserPreferencesService(msalInstance);
